
import { useState } from "react";
import { Plus, List, Layout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { TasksTable } from "@/components/tasks/TasksTable";
import { TasksKanban } from "@/components/tasks/TasksKanban";
import { TasksFilters } from "@/components/tasks/TasksFilters";
import { useOptimizedTasks } from "@/hooks/useOptimizedTasks";
import { useEmployees } from "@/hooks/useEmployees";
import { useClients } from "@/hooks/useClients";
import { useSuppliers } from "@/hooks/useSuppliers";
import { useCompanies } from "@/hooks/useCompanies";
import { useTaskTypes } from "@/hooks/useTaskTypes";
import { useCreateClient } from "@/hooks/useCreateClient";
import { useUpdateClient } from "@/hooks/useUpdateClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import DashboardLoading from "@/components/dashboard/DashboardLoading";
import { Task } from "@/types/task";

const Tasks = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");

  const { tasks, isLoading } = useOptimizedTasks();
  const { employees } = useEmployees();
  const { data: clients } = useClients();
  const { data: suppliers } = useSuppliers();
  const { data: companies } = useCompanies();
  const { taskTypes } = useTaskTypes();
  const queryClient = useQueryClient();

  const createTaskMutation = useMutation({
    mutationFn: async (taskData: any) => {
      const { data, error } = await supabase
        .from('tasks')
        .insert([taskData])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-tasks'] });
      toast.success("تم إنشاء المهمة بنجاح");
    },
    onError: (error: any) => {
      toast.error(error.message || "حدث خطأ أثناء إنشاء المهمة");
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-tasks'] });
      toast.success("تم تحديث المهمة بنجاح");
    },
    onError: (error: any) => {
      toast.error(error.message || "حدث خطأ أثناء تحديث المهمة");
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-tasks'] });
      toast.success("تم حذف المهمة بنجاح");
    },
    onError: (error: any) => {
      toast.error(error.message || "حدث خطأ أثناء حذف المهمة");
    },
  });

  const handleCreateTask = async (taskData: any) => {
    try {
      await createTaskMutation.mutateAsync(taskData);
      setIsDialogOpen(false);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleUpdateTask = async (taskData: any) => {
    if (editingTask) {
      try {
        await updateTaskMutation.mutateAsync({ id: editingTask.id, ...taskData });
        setIsDialogOpen(false);
        setEditingTask(null);
      } catch (error) {
        // Error handling is done in the mutation
      }
    }
  };

  const handleTaskClick = (task: Task) => {
    setEditingTask(task);
    setIsDialogOpen(true);
  };

  const handleDeleteTask = (taskId: string) => {
    deleteTaskMutation.mutate(taskId);
  };

  const handleTaskStatusChange = (taskId: string, newStatus: Task['status']) => {
    updateTaskMutation.mutate({ id: taskId, status: newStatus });
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPriorityFilter("all");
    setAssignedToFilter("all");
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = !searchTerm || 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === "all" || task.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || task.priority === priorityFilter;
    const matchesAssignedTo = assignedToFilter === "all" || task.assigned_to === assignedToFilter;

    return matchesSearch && matchesStatus && matchesPriority && matchesAssignedTo;
  });

  if (isLoading) {
    return <DashboardLoading />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">إدارة المهام</h1>
            <p className="text-slate-600">تنظيم ومتابعة جميع المهام والأنشطة</p>
          </div>
          <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            مهمة جديدة
          </Button>
        </div>

        <TasksFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          priorityFilter={priorityFilter}
          onPriorityChange={setPriorityFilter}
          assignedToFilter={assignedToFilter}
          onAssignedToChange={setAssignedToFilter}
          onClearFilters={handleClearFilters}
        />

        <Tabs defaultValue="table" className="space-y-6">
          <TabsList className="grid w-fit grid-cols-2">
            <TabsTrigger value="table" className="gap-2">
              <List className="w-4 h-4" />
              عرض جدولي
            </TabsTrigger>
            <TabsTrigger value="kanban" className="gap-2">
              <Layout className="w-4 h-4" />
              لوحة كانبان
            </TabsTrigger>
          </TabsList>

          <TabsContent value="table">
            <TasksTable
              tasks={filteredTasks}
              onTaskClick={handleTaskClick}
            />
          </TabsContent>

          <TabsContent value="kanban">
            <TasksKanban
              tasks={filteredTasks}
              onTaskStatusChange={handleTaskStatusChange}
              onTaskClick={handleTaskClick}
            />
          </TabsContent>
        </Tabs>

        <TaskDialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingTask(null);
            }
          }}
          onSuccess={() => {
            setIsDialogOpen(false);
            setEditingTask(null);
          }}
          task={editingTask}
          taskTypes={taskTypes}
        />
      </div>
    </div>
  );
};

export default Tasks;
